import React from 'react';

import CheckboxField from './formElements/CheckboxField';
import DateField from './formElements/DateField';
import DropdownField from './formElements/DropdownField';
import ErrorField from './formElements/ErrorField';
import PasswordField from './formElements/PasswordField';
import TextField from './formElements/TextField';
import MultiSelectField from './formElements/MultiSelectField';
import ColorPickerField from './formElements/ColorPickerField';
import TextAreaField from './formElements/TextAreaField';

interface FormFieldProps {
  id: string;
  label: string;
  value?: string | boolean | number | Date | string[];
  onChange: (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => void;
  placeholder?: string;
  type?:
    | 'text'
    | 'textarea'
    | 'email'
    | 'password'
    | 'number'
    | 'dropdown'
    | 'checkbox'
    | 'date'
    | 'datetime'
    | 'richtext'
    | 'multiselect'
    | 'colorpicker'; // Add this new type
  options?: {
    value: string | undefined;
    label: string | React.ReactNode;
    description?: string;
    searchText?: string;
  }[];
  error?: string;
  disabled?: boolean;
  maxLength?: number;
  helper?: string | React.ReactNode;
  required?: boolean;
  onClick?: () => void; // Add this new prop
  rows?: number;  // Add this new prop
  isLoading?: boolean; // Add isLoading prop
}

const FormField: React.FC<FormFieldProps> = ({
  id,
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
  options,
  error,
  disabled = false,
  maxLength,
  helper,
  required,
  onClick, // Add this new prop
  rows = 4,  // Add this new prop with default value
  isLoading,
}) => (
  <div className="mb-6 text-left">
    <label
      htmlFor={id}
      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
    >
      {label}
      {required && <span className="text-red-500 ml-1">*</span>}
    </label>
    {type === 'dropdown' && options && (
      <DropdownField
        id={id}
        value={value as string}
        onChange={onChange}
        options={options}
        disabled={disabled}
        placeholder={placeholder}
        isLoading={isLoading}
      />
    )}
    {['date', 'datetime'].includes(type) && (
      <DateField
        id={id}
        value={value as string}
        onChange={onChange}
        disabled={disabled}
      />
    )}
    {type === 'checkbox' && (
      <CheckboxField
        id={id}
        value={value as boolean}
        onChange={onChange}
        disabled={disabled}
      />
    )}
    {['text', 'email', 'number'].includes(type) && (
      <TextField
        id={id}
        type={type}
        value={value as string}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        maxLength={maxLength}
        onClick={onClick} // Pass the onClick handler
      />
    )}
    {type === 'password' && (
      <PasswordField
        id={id}
        value={value as string}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
      />
    )}
    {type === 'textarea' && (
      <TextAreaField
        id={id}
        value={value as string}
        onChange={onChange as (e: React.ChangeEvent<HTMLTextAreaElement>) => void}
        placeholder={placeholder}
        disabled={disabled}
        maxLength={maxLength}
        rows={rows}
      />
    )}
    {type === 'multiselect' && options && (
      <MultiSelectField
        id={id}
        value={value as string[]}
        onChange={(values) => {
          const e = {
            target: {
              id,
              value: values,
              type: 'multiselect'
            }
          } as unknown as React.ChangeEvent<HTMLInputElement>;
          onChange(e);
        }}
        options={options.map(opt => ({
          value: opt.value,
          label: opt.label,
          description: opt.description,
          searchText: opt.searchText || (typeof opt.label === 'string' ? opt.label : '')
        }))}
        disabled={disabled}
        placeholder={placeholder}
      />
    )}
    {type === 'colorpicker' && (
      <ColorPickerField
        id={id}
        value={value as string}
        onChange={onChange}
        disabled={disabled}
      />
    )}
    {helper && (
      <p className="mt-1 text-xs text-gray-400 dark:text-gray-400">
        {helper}
      </p>
    )}
    {error && <ErrorField error={error} />}
  </div>
);

export default FormField;
